import { StackingCards as StackingCardsType } from "../../../sanity/lib/types/stackingCards";
import { useGSAP } from "@gsap/react";
import { useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";
import { StackingCard } from "./StackingCard";

gsap.registerPlugin(ScrollTrigger);

type Props = StackingCardsType;

export default function (props: Props) {
    const containerRef = useRef<HTMLDivElement>(null);

    const sliderCount =
        props.customCards?.length || props.serviceCards?.length || props.jobCards?.length || 0;

    useGSAP(
        () => {
            const mm = gsap.matchMedia();
            mm.add("(min-width: 768px)", () => {
                const cards = gsap.utils.toArray(".card");

                ScrollTrigger.create({
                    trigger: cards[0] as any,
                    start: "top 10%",
                    endTrigger: cards[cards.length - 1] as any,
                    end: "top 75%",
                    pin: ".intro",
                    pinSpacing: false,
                });

                cards.forEach((card, index) => {
                    const isLastCard = index === cards.length - 1;
                    const cardInner = (card as HTMLElement).querySelector(".card-inner");

                    if (!isLastCard) {
                        ScrollTrigger.create({
                            trigger: card as HTMLElement,
                            start: "bottom 80%",
                            endTrigger: ".outro",
                            end: "top 90%",
                            pin: true,
                            pinSpacing: false,
                        });

                        gsap.to(cardInner, {
                            y: `-${(cards.length - index) * 15}vh`,
                            ease: "none",
                            scrollTrigger: {
                                trigger: card as HTMLElement,
                                start: "bottom 80%",
                                endTrigger: ".outro",
                                end: "top 90%",
                                scrub: true,
                            },
                        });
                    }
                });
            });
            return () => {
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            };
        },
        { scope: containerRef },
    );

    return (
        <section ref={containerRef}>
            <h2 className="absolute -top-[100vh]">{props.title}</h2>
            <div className="stacking-cards-wrapper">
                <div className="relative md:mt-20 stacking-cards">
                    <div className="intro"></div>

                    {props.type === "custom" &&
                        props.customCards?.map((card, index) => (
                            <StackingCard
                                key={card._key}
                                uid={card._key}
                                title={card.cardTitle || ""}
                                description={card.cardDescription}
                                subitems={card.listItems}
                                link={card.link}
                                index={index}
                                images={card.images}
                                titleSize="small"
                            />
                        ))}
                    {props.type === "service" &&
                        props.serviceCards?.map((service, index) => (
                            <StackingCard
                                key={service._key}
                                uid={service._key}
                                title={service.service?.title || ""}
                                description={service.service?.description}
                                subitems={service.service?.serviceCategories.map(
                                    (title) => title.title,
                                )}
                                link={service.link}
                                index={index}
                                images={service.images}
                            />
                        ))}
                    {props.type === "job" &&
                        props.jobCards?.map((job, index) => (
                            <StackingCard
                                key={job._key}
                                uid={job._key}
                                title={job.job?.title || ""}
                                description={job.description}
                                subitems={job.job?.facts.map((title) => title.value)}
                                link={{
                                    label: job.link || "",
                                    _type: "link",
                                    type: "internal",
                                    internal: {
                                        metadata: {
                                            slug: {
                                                current: "jobs/" + job.job?.slug,
                                            },
                                        },
                                    },
                                }}
                                index={index}
                                images={job.images}
                                titleSize="small"
                            />
                        ))}
                    {props.additionalSlides?.map((slide, index) => (
                        <StackingCard
                            key={slide._key}
                            uid={slide._key}
                            title={slide.title || ""}
                            description={slide.cardDescription}
                            subitems={slide.listItems}
                            link={slide.link}
                            index={sliderCount + index}
                            images={slide.images}
                            titleSize="small"
                        />
                    ))}
                    <div className="outro md:h-[1px]"></div>
                </div>
            </div>
        </section>
    );
}
